import { useEffect } from 'react';

/**
 * The browser tab title (V3-043): "[Page] – DRM WRAP", with the environment outside production
 * ("Projects – DRM WRAP – Testing"). En dashes throughout (LANG-05).
 *
 * Two sources, one rule:
 *   - every route has a page title (config/pageTitles.ts), applied by the app shell on navigation
 *     (setRouteDocumentTitle), so no page is left on the bare "DRM WRAP";
 *   - a page that knows a more specific name (a vendor, a unit) calls useDocumentTitle, which
 *     overrides the route title while that page is mounted.
 */

const VALID_STAGES = ['development', 'staging', 'training', 'testing', 'production'];

function releaseStage(): string {
  const env = (process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development').toLowerCase();
  return VALID_STAGES.includes(env) ? env : 'development';
}

/** "DRM WRAP", plus the environment outside production ("DRM WRAP – Testing"). */
export function baseDocumentTitle(): string {
  const stage = releaseStage();
  return stage === 'production' ? 'DRM WRAP' : `DRM WRAP – ${stage.charAt(0).toUpperCase()}${stage.slice(1)}`;
}

/** "[Page] – DRM WRAP[ – Environment]", or the base title when there is no page name. */
export function formatDocumentTitle(page: string | null | undefined): string {
  const base = baseDocumentTitle();
  return page ? `${page} – ${base}` : base;
}

let routeTitle: string | null = null;
// Most recent last; a stack so nested or overlapping overrides unwind cleanly.
const overrides: Array<{ title: string }> = [];

function applyDocumentTitle(): void {
  if (typeof document === 'undefined') return;
  const override = overrides.length ? overrides[overrides.length - 1].title : null;
  document.title = formatDocumentTitle(override ?? routeTitle);
}

/** The app shell's route title (App.tsx Layout). */
export function setRouteDocumentTitle(title: string | null): void {
  routeTitle = title;
  applyDocumentTitle();
}

/**
 * A page-specific tab title ("Acme Paving – DRM WRAP") while the calling page is mounted. Falls
 * back to the route's title when it unmounts or the title is empty.
 */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    if (!title) return;
    const entry = { title };
    overrides.push(entry);
    applyDocumentTitle();
    return () => {
      const i = overrides.indexOf(entry);
      if (i >= 0) overrides.splice(i, 1);
      applyDocumentTitle();
    };
  }, [title]);
}
